import { useState, useEffect } from 'react';
import LegalLayout from './LegalLayout';

function LGPD() {
  const [html, setHtml] = useState<string | null>(null);
  const [css, setCss] = useState('');

  useEffect(() => {
    fetch('/api/conteudo/pagina/lgpd')
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((data) => {
        if (data.html && data.html.trim()) {
          setHtml(data.html);
          setCss(data.css || '');
        }
      })
      .catch(() => {});
  }, []);

  if (html) {
    return (
      <LegalLayout>
        <style dangerouslySetInnerHTML={{ __html: css }} />
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </LegalLayout>
    );
  }

  return (
    <LegalLayout>
      <h1>LGPD — Lei Geral de Proteção de Dados</h1>
      <p className="legal-last-updated">Última atualização: Janeiro de 2025</p>

      <p>
        A <strong>Vavassori Designer</strong> está em conformidade com a Lei Geral de Proteção de
        Dados Pessoais (Lei nº 13.709/2018 — LGPD). Esta página detalha como tratamos dados
        pessoais e como você pode exercer seus direitos como titular.
      </p>

      <h2>1. Base Legal para Tratamento de Dados</h2>
      <p>
        Realizamos o tratamento de dados pessoais com base nas seguintes hipóteses legais
        previstas no Art. 7º da LGPD:
      </p>
      <ul>
        <li>
          <strong>Execução de contrato (Art. 7º, V):</strong> tratamento necessário para
          execução do contrato de prestação de serviços de acompanhamento de obras
        </li>
        <li>
          <strong>Consentimento (Art. 7º, I):</strong> para coleta de dados adicionais não
          essenciais à prestação do serviço, como envio de comunicações informativas
        </li>
        <li>
          <strong>Legítimo interesse (Art. 7º, IX):</strong> para segurança da plataforma,
          prevenção de fraudes, melhoria dos serviços e análises internas de uso
        </li>
        <li>
          <strong>Cumprimento de obrigação legal (Art. 7º, II):</strong> para atendimento a
          requisitos regulatórios e obrigações fiscais
        </li>
      </ul>

      <h2>2. Encarregado de Proteção de Dados (DPO)</h2>
      <p>
        Nosso Encarregado de Proteção de Dados está disponível para esclarecer dúvidas e receber
        solicitações relacionadas ao tratamento de dados pessoais:
      </p>
      <ul>
        <li>
          <strong>Responsável:</strong> Vavassori Designer — Setor de Privacidade
        </li>
        <li>
          <strong>E-mail:</strong>{' '}
          <a href="mailto:privacidade@vavassoridesigner.com.br">
            privacidade@vavassoridesigner.com.br
          </a>
        </li>
        <li>
          <strong>Endereço:</strong> Joinville, SC — Brasil
        </li>
      </ul>

      <h2>3. Direitos do Titular dos Dados</h2>
      <p>
        Conforme previsto nos Arts. 17 a 22 da LGPD, você possui os seguintes direitos:
      </p>
      <ul>
        <li>
          <strong>Confirmação e acesso:</strong> confirmar a existência de tratamento e obter
          acesso aos seus dados pessoais
        </li>
        <li>
          <strong>Correção:</strong> solicitar a correção de dados incompletos, inexatos ou
          desatualizados
        </li>
        <li>
          <strong>Anonimização, bloqueio ou eliminação:</strong> para dados desnecessários,
          excessivos ou tratados em desconformidade com a LGPD
        </li>
        <li>
          <strong>Portabilidade:</strong> transferência dos seus dados a outro fornecedor de
          serviço, mediante requisição expressa
        </li>
        <li>
          <strong>Eliminação:</strong> dos dados tratados com base em consentimento, exceto nas
          hipóteses de conservação previstas em lei
        </li>
        <li>
          <strong>Informação sobre compartilhamento:</strong> saber com quais entidades públicas e
          privadas seus dados são compartilhados
        </li>
        <li>
          <strong>Revogação do consentimento:</strong> retirar o consentimento a qualquer momento,
          de forma gratuita e facilitada
        </li>
        <li>
          <strong>Oposição:</strong> opor-se a tratamento realizado com base em hipóteses legais
          que não o consentimento, em caso de descumprimento da LGPD
        </li>
      </ul>

      <h2>4. Como Exercer seus Direitos</h2>
      <p>Para exercer qualquer um dos direitos acima, você pode:</p>
      <ol>
        <li>
          Enviar e-mail para{' '}
          <a href="mailto:privacidade@vavassoridesigner.com.br">
            privacidade@vavassoridesigner.com.br
          </a>{' '}
          com o assunto "Exercício de Direitos LGPD"
        </li>
        <li>Incluir seu nome completo e e-mail cadastrado na plataforma</li>
        <li>Descrever qual direito deseja exercer</li>
        <li>Aguardar resposta em até 15 (quinze) dias úteis</li>
      </ol>
      <p>
        Poderemos solicitar comprovação de identidade para garantir que a solicitação é feita pelo
        titular legítimo dos dados.
      </p>

      <h2>5. Medidas de Segurança</h2>
      <p>
        Adotamos medidas técnicas e administrativas aptas a proteger os dados pessoais de acessos
        não autorizados e de situações acidentais ou ilícitas, incluindo:
      </p>
      <ul>
        <li>Criptografia de dados em trânsito (TLS 1.2+) e em repouso</li>
        <li>Controle de acesso baseado em perfis com princípio do menor privilégio</li>
        <li>Autenticação segura com hashing de senhas (bcrypt)</li>
        <li>Monitoramento contínuo e logs de auditoria de acessos</li>
        <li>Proteção contra ataques de força bruta (rate limiting)</li>
        <li>Backups criptografados com rotina automatizada</li>
        <li>Revisões periódicas de segurança</li>
      </ul>

      <h2>6. Transferência Internacional de Dados</h2>
      <p>
        Os dados pessoais são armazenados e processados em servidores localizados no Brasil. Em
        caso de necessidade de transferência internacional (como serviços de infraestrutura em
        nuvem), garantimos que os destinatários mantenham nível de proteção compatível com a LGPD,
        conforme Art. 33 da lei.
      </p>

      <h2>7. Incidentes de Segurança</h2>
      <p>
        Em caso de incidente de segurança que possa acarretar risco ou dano relevante aos
        titulares, comunicaremos:
      </p>
      <ul>
        <li>A Autoridade Nacional de Proteção de Dados (ANPD)</li>
        <li>Os titulares afetados, em prazo razoável</li>
      </ul>
      <p>
        A comunicação conterá a descrição da natureza dos dados afetados, os riscos relacionados,
        as medidas adotadas e as medidas para reverter ou mitigar os efeitos do incidente.
      </p>

      <h2>8. Retenção e Eliminação</h2>
      <p>
        Os dados pessoais são retidos apenas pelo tempo necessário para cumprimento das finalidades
        informadas. Após o término do tratamento, os dados serão eliminados, salvo nas hipóteses
        de conservação previstas no Art. 16 da LGPD:
      </p>
      <ul>
        <li>Cumprimento de obrigação legal ou regulatória</li>
        <li>Estudo por órgão de pesquisa (anonimizados)</li>
        <li>Uso exclusivo do controlador (anonimizados)</li>
      </ul>

      <h2>9. Autoridade Nacional de Proteção de Dados</h2>
      <p>
        Caso considere que o tratamento de seus dados pessoais viola a LGPD, você tem o direito de
        apresentar reclamação perante a Autoridade Nacional de Proteção de Dados (ANPD):
      </p>
      <p>
        <strong>Site:</strong>{' '}
        <a href="https://www.gov.br/anpd" target="_blank" rel="noopener noreferrer">
          www.gov.br/anpd
        </a>
      </p>

      <h2>10. Atualização desta Página</h2>
      <p>
        Este documento pode ser atualizado para refletir alterações em nossas práticas ou na
        legislação vigente. A data da última atualização estará sempre indicada no topo desta
        página.
      </p>
    </LegalLayout>
  );
}

export default LGPD;
